import React, { useState, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type {
  QualityAssuranceReportingProps,
  QualityReportingData
} from '../../types/advanced_content_quality_ui';

// Report type configuration
const REPORT_TYPES = {
  operational: {
    label: 'Operational Report',
    description: 'Daily quality metrics and operational insights',
    icon: '⚙️',
    color: 'blue'
  },
  strategic: {
    label: 'Strategic Report', 
    description: 'Long-term quality trends and strategic insights',
    icon: '🎯',
    color: 'purple'
  },
  compliance: {
    label: 'Compliance Report',
    description: 'Quality compliance and audit documentation',
    icon: '📋',
    color: 'green'
  }
};

// Export format configuration
const EXPORT_FORMATS = {
  pdf: { label: 'PDF Document', icon: '📄', mime: 'application/pdf' },
  excel: { label: 'Excel Spreadsheet', icon: '📊', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  json: { label: 'JSON Data', icon: '📋', mime: 'application/json' },
  csv: { label: 'CSV Data', icon: '📈', mime: 'text/csv' }
};

export const QualityAssuranceReporting: React.FC<QualityAssuranceReportingProps> = ({
  reportingData,
  reportType = 'operational',
  exportOptions = ['pdf', 'excel'],
  onReportGenerate
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'insights' | 'performance' | 'audit'>('overview');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState(reportType);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const { overallPerformance } = reportingData.comprehensiveAnalytics;
    
    return {
      totalContent: 150, // This would come from the actual data
      qualityScore: overallPerformance?.averageQuality || 0,
      complianceRate: overallPerformance?.complianceRate || 0,
      improvementTrend: overallPerformance?.improvementTrend || 0
    };
  }, [reportingData.comprehensiveAnalytics]);

  // Handle report generation
  const handleGenerateReport = async (format: string) => {
    if (!onReportGenerate) return;

    setGeneratingReport(true);
    try {
      await onReportGenerate(selectedReportType, {
        format,
        dateRange,
        includeCharts: true,
        includeAuditTrail: selectedReportType === 'compliance'
      });
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setGeneratingReport(false);
    }
  };

  // Render key findings
  const renderKeyFindings = () => {
    const { keyFindings } = reportingData.detailedInsights;
    
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Findings</h3>
        <div className="space-y-4">
          {keyFindings.map((finding, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  finding.severity === 'critical' ? 'bg-red-500' :
                  finding.severity === 'high' ? 'bg-orange-500' :
                  finding.severity === 'medium' ? 'bg-yellow-500' :
                  'bg-blue-500'
                }`} />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{finding.title}</h4>
                  <p className="text-sm text-gray-700 mb-2">{finding.description}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>Category: {finding.category}</span>
                    <span>Impact: {finding.impact}</span>
                    <span>Confidence: {(finding.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // Render actionable recommendations
  const renderActionableRecommendations = () => {
    const { actionableRecommendations } = reportingData.detailedInsights;
    
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actionable Recommendations</h3>
        <div className="space-y-4">
          {actionableRecommendations.map((recommendation, index) => (
            <div key={index} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-blue-900">{recommendation.title}</h4>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  recommendation.priority === 'critical' ? 'bg-red-100 text-red-800' :
                  recommendation.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                  recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {recommendation.priority}
                </span>
              </div>
              <p className="text-sm text-blue-800 mb-3">{recommendation.description}</p>
              <div className="space-y-2">
                <div className="text-sm text-blue-700">
                  <span className="font-medium">Expected Impact:</span> {recommendation.expectedImpact}
                </div>
                <div className="text-sm text-blue-700">
                  <span className="font-medium">Implementation:</span> {recommendation.implementation}
                </div>
                <div className="text-sm text-blue-700">
                  <span className="font-medium">Timeline:</span> {recommendation.timeline}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // Render quality risks
  const renderQualityRisks = () => {
    const { riskIdentification } = reportingData.detailedInsights;
    
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Risks</h3>
        <div className="space-y-4">
          {riskIdentification.map((risk, index) => (
            <div key={index} className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-red-900">{risk.riskType}</h4>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-red-700">
                    Probability: {(risk.probability * 100).toFixed(0)}%
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    risk.severity === 'critical' ? 'bg-red-200 text-red-900' :
                    risk.severity === 'high' ? 'bg-orange-200 text-orange-900' :
                    'bg-yellow-200 text-yellow-900'
                  }`}>
                    {risk.severity}
                  </span>
                </div>
              </div>
              <p className="text-sm text-red-800 mb-3">{risk.description}</p>
              <div className="space-y-1">
                <div className="text-sm text-red-700">
                  <span className="font-medium">Impact:</span> {risk.impact}
                </div>
                <div className="text-sm text-red-700">
                  <span className="font-medium">Mitigation:</span> {risk.mitigation}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // Render performance metrics
  const renderPerformanceMetrics = () => {
    const { performanceReporting } = reportingData;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{summaryStats.totalContent}</div>
            <div className="text-sm text-gray-600">Total Content</div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{summaryStats.qualityScore.toFixed(1)}%</div>
            <div className="text-sm text-gray-600">Avg Quality Score</div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{summaryStats.complianceRate.toFixed(1)}%</div>
            <div className="text-sm text-gray-600">Compliance Rate</div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              summaryStats.improvementTrend >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {summaryStats.improvementTrend >= 0 ? '+' : ''}{summaryStats.improvementTrend.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Improvement Trend</div>
          </div>
        </Card>
      </div>
    );
  };

  // Render audit trail
  const renderAuditTrail = () => {
    const { auditTrail } = reportingData;
    
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Quality Events</h3>
        <div className="space-y-3">
          {auditTrail.qualityEvents.slice(0, 10).map((event: { id?: string; type?: string; message?: string; timestamp?: string }, index: number) => (
            <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${
                event.type === 'quality_improvement' ? 'bg-green-500' :
                event.type === 'quality_degradation' ? 'bg-red-500' :
                event.type === 'threshold_breach' ? 'bg-orange-500' :
                'bg-blue-500'
              }`} />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">{event.event}</span>
                  <span className="text-sm text-gray-500">{event.timestamp.toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-600">{event.description}</p>
              </div>
              {event.user && (
                <span className="text-sm text-gray-500">By: {event.user}</span>
              )}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'insights', label: 'Insights', icon: '💡' },
    { id: 'performance', label: 'Performance', icon: '⚡' },
    { id: 'audit', label: 'Audit Trail', icon: '📋' }
  ] as const;

  return (
    <div className="quality-assurance-reporting space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Quality Assurance Reporting</h2>
          <p className="text-gray-600 mt-1">Comprehensive quality analytics and insights</p>
        </div>
        
        {/* Report Controls */}
        <div className="flex items-center space-x-3">
          <select
            value={selectedReportType}
            onChange={(e) => setSelectedReportType(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {Object.entries(REPORT_TYPES).map(([key, type]) => (
              <option key={key} value={key}>
                {type.icon} {type.label}
              </option>
            ))}
          </select>
          
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Report Type Description */}
      <Card className={`p-4 bg-${REPORT_TYPES[selectedReportType].color}-50 border-${REPORT_TYPES[selectedReportType].color}-200`}>
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{REPORT_TYPES[selectedReportType].icon}</span>
          <div>
            <h3 className={`font-semibold text-${REPORT_TYPES[selectedReportType].color}-900`}>
              {REPORT_TYPES[selectedReportType].label}
            </h3>
            <p className={`text-sm text-${REPORT_TYPES[selectedReportType].color}-700`}>
              {REPORT_TYPES[selectedReportType].description}
            </p>
          </div>
        </div>
      </Card>

      {/* Export Options */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Export Report</h3>
            <p className="text-sm text-gray-600">Generate and download quality reports</p>
          </div>
          <div className="flex items-center space-x-2">
            {exportOptions.map((format: string) => (
              <Button
                key={format}
                size="small"
                variant="ghost"
                onClick={() => handleGenerateReport(format)}
                disabled={generatingReport}
              >
                {generatingReport ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <span className="mr-1">{(EXPORT_FORMATS as any)[format]?.icon}</span>
                    {(EXPORT_FORMATS as any)[format]?.label}
                  </>
                )}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {renderPerformanceMetrics()}
            {renderKeyFindings()}
          </div>
        )}
        
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {renderPerformanceMetrics()}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Analytics</h3>
              <p className="text-gray-600">Advanced analytics charts and visualizations would be rendered here.</p>
            </Card>
          </div>
        )}
        
        {activeTab === 'insights' && (
          <div className="space-y-6">
            {renderKeyFindings()}
            {renderActionableRecommendations()}
            {renderQualityRisks()}
          </div>
        )}
        
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {renderPerformanceMetrics()}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Dashboard</h3>
              <p className="text-gray-600">Performance metrics and KPI dashboards would be rendered here.</p>
            </Card>
          </div>
        )}
        
        {activeTab === 'audit' && (
          <div className="space-y-6">
            {renderAuditTrail()}
          </div>
        )}
      </div>
    </div>
  );
};

export default QualityAssuranceReporting;
